/**
 * useEarned — Three-pool earned value from profile (server-computed)
 *
 * Reads earnedThisMonthCents from the user profile (written by
 * updateEarnedForUser Cloud Function). Falls back to 0 when
 * earnedMonth doesn't match the current month (stale data).
 *
 * This replaces the client-side ART_INCOME query in useBurnProgress
 * and the verdict-only computation in StudioTab.
 */

import { useAuth } from '@/contexts/AuthContext';

interface EarnedReturn {
  earnedCents: number;
  breakdown: { pool1: number; pool2: number; pool3: number };
  lastUpdated: string | null;
  isStale: boolean;
}

export function useEarned(): EarnedReturn {
  const { profile } = useAuth();

  // Current month key
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // If earnedMonth doesn't match current month, data is stale — return 0
  const isStale = !profile?.earnedMonth || profile.earnedMonth !== currentMonth;

  return {
    earnedCents: isStale ? 0 : (profile?.earnedThisMonthCents ?? 0),
    breakdown: isStale
      ? { pool1: 0, pool2: 0, pool3: 0 }
      : (profile?.earnedBreakdown ?? { pool1: 0, pool2: 0, pool3: 0 }),
    lastUpdated: profile?.earnedLastUpdated ?? null,
    isStale,
  };
}

export default useEarned;
